import { LegalLayout, LegalList, LegalSection } from "../../components/public/legal-layout";

const lastUpdated = "24 March 2026";

export default function LegalTermsPage({ auth }) {
  return (
    <LegalLayout
      auth={auth}
      eyebrow="Legal"
      title="Terms & Conditions"
      intro="These Terms & Conditions govern access to and use of UGC Persona, a software product operated by MDG. They are written for a global customer base and are intended to provide a clear baseline for subscriptions, credits, AI generation workflows, and acceptable use."
    >
      <LegalSection title="1. Acceptance of Terms">
        <p>
          By accessing or using UGC Persona, you agree to these Terms & Conditions. If you are using the service on behalf of a company,
          agency, or client, you confirm that you have authority to bind that organization to these terms.
        </p>
        <p>
          If you do not agree, do not use the service. Mandatory consumer protection rights that apply in your country of residence remain
          unaffected.
        </p>
      </LegalSection>

      <LegalSection title="2. About the Service">
        <p>
          UGC Persona is a web application that helps users generate UGC-style assets and videos using third-party AI providers. Depending on
          the workflow selected, the service may include image generation, image editing, copy generation, voice generation, and final video
          generation.
        </p>
        <p>
          Certain outputs are produced through integrated external providers including, where configured, fal.ai, Wavespeed, OpenRouter,
          ElevenLabs, and Stripe.
        </p>
      </LegalSection>

      <LegalSection title="3. Accounts and Eligibility">
        <LegalList
          items={[
            "You must provide accurate registration information and keep your credentials secure.",
            "You are responsible for all activity performed through your account.",
            "You may not share access in a way that exceeds your permitted plan or intended usage model.",
            "MDG may suspend or restrict access if an account is used fraudulently, unlawfully, or in a way that creates security or billing risk."
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Plans, Subscriptions, and Credits">
        <p>UGC Persona currently offers monthly subscription plans and a bring-your-own-API model for specific business customers.</p>
        <LegalList
          items={[
            "Paid plans renew monthly unless cancelled.",
            "Credits reset at each monthly renewal and do not roll over.",
            "Additional credit packs are not offered at this stage unless explicitly stated otherwise.",
            "The number of credits consumed depends on the operation performed, with video generation being the main variable component."
          ]}
        />
        <p>
          Video generation is billed internally using the actual generated duration, rounded up to the next full second. Other steps such as
          image generation, script generation, voice generation, and voice optimization may consume fixed credits per request according to the
          billing rules then in force.
        </p>
      </LegalSection>

      <LegalSection title="5. Billing and Provider Usage">
        <p>
          Credits are deducted only where the relevant provider request is actually processed and billed, or otherwise recorded by the platform
          as chargeable usage under the active pricing logic.
        </p>
        <p>
          Certain retries, failed provider responses, or interrupted requests may not consume credits if they are not actually processed.
          Provider-side behavior may vary, and MDG reserves the right to reconcile usage when required to reflect actual billed operations.
        </p>
      </LegalSection>

      <LegalSection title="6. AI Outputs and User Responsibility">
        <p>
          UGC Persona is a generation and orchestration tool. AI outputs may be inaccurate, incomplete, non-unique, or unsuitable for your
          intended use. You are responsible for reviewing all generated content before publication, distribution, or commercial use.
        </p>
        <LegalList
          items={[
            "Generated outputs should not be treated as legal, medical, financial, or regulatory advice.",
            "You are responsible for ensuring that your prompts, assets, and final outputs comply with advertising, disclosure, IP, privacy, and platform rules.",
            "You must not use the service to create unlawful, misleading, defamatory, infringing, abusive, or harmful material."
          ]}
        />
      </LegalSection>

      <LegalSection title="7. Acceptable Use">
        <LegalList
          items={[
            "Do not upload or generate content that violates applicable law or third-party rights.",
            "Do not attempt to reverse engineer, abuse, overload, scrape, or bypass platform limits or provider controls.",
            "Do not use the service for impersonation, fraud, harassment, or deceptive identity-based content.",
            "Do not submit prompts or assets that you do not have the right to use."
          ]}
        />
      </LegalSection>

      <LegalSection title="8. Intellectual Property">
        <p>
          MDG retains all rights in the UGC Persona platform, software, branding, design, and related documentation. Except for the limited
          right to use the service under your plan, no intellectual property rights are transferred to you.
        </p>
        <p>
          As between you and MDG, you retain responsibility for the materials you upload. Rights in generated outputs may depend on the terms
          of the third-party provider involved and on applicable law. You are responsible for verifying whether a generated output may be used
          commercially, modified, or redistributed.
        </p>
      </LegalSection>

      <LegalSection title="9. Availability, Changes, and Support">
        <p>
          The service may evolve over time. MDG may add, remove, or change workflows, providers, plan features, or pricing, including when
          necessary due to infrastructure, provider, compliance, or product reasons.
        </p>
        <p>
          The platform is provided on an evolving software basis. No uninterrupted availability, exact provider uptime, or perpetual
          compatibility with third-party APIs is guaranteed.
        </p>
      </LegalSection>

      <LegalSection title="10. Suspension and Termination">
        <p>
          MDG may suspend or terminate access if you materially breach these terms, create security or billing abuse, violate provider rules,
          or use the platform unlawfully. You may stop using the service at any time and cancel subscription renewal in accordance with the
          plan controls then available.
        </p>
      </LegalSection>

      <LegalSection title="11. Disclaimers and Limitation of Liability">
        <p>
          To the maximum extent permitted by law, the service is provided on an “as is” and “as available” basis. MDG disclaims warranties of
          merchantability, fitness for a particular purpose, non-infringement, uninterrupted service, and error-free operation.
        </p>
        <p>
          To the maximum extent permitted by law, MDG will not be liable for indirect, incidental, special, consequential, exemplary, or lost
          profit damages, or for losses arising from third-party providers, generated content, account misuse, business interruption, or data
          supplied by users.
        </p>
      </LegalSection>

      <LegalSection title="12. Privacy">
        <p>
          Your use of the service is also subject to the UGC Persona Privacy & Cookies Policy, which explains how MDG handles account data,
          technical data, provider-related processing, and essential cookies.
        </p>
      </LegalSection>

      <LegalSection title="13. Governing Law">
        <p>
          These Terms are governed by the laws applicable where MDG is established, without prejudice to mandatory consumer protections that may
          apply under the law of your usual place of residence.
        </p>
      </LegalSection>

      <LegalSection title="14. Contact">
        <p>
          Service operator: <span className="font-semibold text-white">MDG</span>
        </p>
        <p>
          Contact for legal or contractual matters:{" "}
          <a href="mailto:hello@ugcpersona.com" className="text-cyan-200 underline underline-offset-4">
            hello@ugcpersona.com
          </a>
        </p>
        <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Last updated: {lastUpdated}</p>
      </LegalSection>
    </LegalLayout>
  );
}
